// Frontend/geniac/src/Components/Settings/RetentionOfferConfirmationModal.tsx
import React from 'react';
import { FiCheckCircle, FiX } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

interface RetentionOfferConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  offerType: 'discount' | 'pause' | 'downgrade';
  offerDetails: {
    discountPercent?: number;
    pauseDays?: number;
    targetPlan?: string;
    expiresAt?: string;
    appliedAt?: string;
    message?: string;
  };
}

const RetentionOfferConfirmationModal: React.FC<RetentionOfferConfirmationModalProps> = ({
  isOpen,
  onClose,
  offerType,
  offerDetails
}) => {
  const { t } = useTranslation('settings');

  if (!isOpen) return null;

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getOfferTitle = () => {
    switch (offerType) {
      case 'discount':
        return 'Réduction appliquée !';
      case 'pause':
        return 'Abonnement en pause';
      case 'downgrade':
        return 'Changement de plan programmé';
      default:
        return 'Offre appliquée';
    }
  };

  const getOfferMessage = () => {
    if (offerDetails.message) {
      return offerDetails.message;
    }

    switch (offerType) {
      case 'discount':
        return `Une réduction de ${offerDetails.discountPercent || 20}% sera appliquée sur votre prochaine facture. Cette offre est valable pour un seul cycle de facturation.`;
      case 'pause':
        return `Votre abonnement est maintenant en pause pour ${offerDetails.pauseDays || 30} jours. Aucun paiement ne sera prélevé pendant cette période. L'abonnement reprendra automatiquement le ${formatDate(offerDetails.expiresAt)}.`;
      case 'downgrade':
        return `Votre abonnement passera au plan ${offerDetails.targetPlan?.toUpperCase() || 'STANDARD'} à la fin de votre période de facturation actuelle (${formatDate(offerDetails.expiresAt)}). Vous conservez tous vos avantages actuels jusqu'à cette date.`;
      default:
        return 'Votre offre de rétention a été appliquée avec succès.';
    }
  };

  const getOfferIcon = () => {
    return (
      <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
        <FiCheckCircle className="h-8 w-8 text-green-600" />
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FiX className="h-6 w-6" />
          </button>

          <div>
            {/* Icon */}
            <div className="mb-4">
              {getOfferIcon()}
            </div>

            {/* Title */}
            <div className="text-center mb-4">
              <h3 className="text-2xl font-bold text-gray-900">
                {getOfferTitle()}
              </h3>
            </div>

            {/* Message */}
            <div className="mb-6">
              <p className="text-base text-gray-600 text-center leading-relaxed">
                {getOfferMessage()}
              </p>
            </div>

            {/* Additional details */}
            {offerType === 'discount' && offerDetails.discountPercent && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-green-800">
                    Réduction
                  </span>
                  <span className="text-2xl font-bold text-green-600">
                    -{offerDetails.discountPercent}%
                  </span>
                </div>
                <p className="text-xs text-green-700 mt-2">
                  Valable sur votre prochaine facture uniquement
                </p>
              </div>
            )}

            {offerType === 'pause' && offerDetails.pauseDays && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-800">
                    Durée de la pause
                  </span>
                  <span className="text-2xl font-bold text-blue-600">
                    {offerDetails.pauseDays} jours
                  </span>
                </div>
                <p className="text-xs text-blue-700 mt-2">
                  Reprise automatique le {formatDate(offerDetails.expiresAt)}
                </p>
              </div>
            )}

            {offerType === 'downgrade' && offerDetails.targetPlan && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-purple-800">
                    Nouveau plan
                  </span>
                  <span className="text-xl font-bold text-purple-600">
                    {offerDetails.targetPlan.toUpperCase()}
                  </span>
                </div>
                <p className="text-xs text-purple-700 mt-2">
                  Changement effectif le {formatDate(offerDetails.expiresAt)}
                </p>
              </div>
            )}

            {/* Action button */}
            <div className="mt-6">
              <button
                onClick={onClose}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-3 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                Compris
              </button>
            </div>

            {/* Help text */}
            <p className="text-xs text-gray-500 text-center mt-4">
              Vous pouvez consulter les détails de votre abonnement dans la page Abonnement à tout moment.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetentionOfferConfirmationModal;
